import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../infrastructure/api/client";
import {
  ApiError,
  type SpotResponseDto,
  type TripDetailResponseDto,
  type TravelMode,
} from "@tabipla/shared";

// Create spot
interface CreateSpotInput {
  name: string;
  description?: string | null;
  category: "sightseeing" | "food" | "activity" | "transport" | "accommodation";
  startTime?: string | null;
  endTime?: string | null;
  location?: string | null;
  placeId?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  formattedAddress?: string | null;
  cost?: number | null;
  notes?: string | null;
  order?: number;
}

export function useCreateSpot(tripId: string, itineraryId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateSpotInput) => {
      const response = await apiClient.post<SpotResponseDto>(
        `/api/trips/${tripId}/itineraries/${itineraryId}/spots`,
        input
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to create spot");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trips", tripId] });
    },
  });
}

// Update spot
interface UpdateSpotInput {
  name?: string;
  description?: string | null;
  category?: "sightseeing" | "food" | "activity" | "transport" | "accommodation";
  startTime?: string | null;
  endTime?: string | null;
  location?: string | null;
  placeId?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  formattedAddress?: string | null;
  cost?: number | null;
  notes?: string | null;
}

export function useUpdateSpot(tripId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ spotId, input }: { spotId: string; input: UpdateSpotInput }) => {
      const response = await apiClient.put<SpotResponseDto>(
        `/api/trips/${tripId}/spots/${spotId}`,
        input
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to update spot");
      }
      return response.data;
    },
    onMutate: async ({ spotId, input }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["trips", tripId] });

      // Snapshot previous value
      const previousTripDetail = queryClient.getQueryData<TripDetailResponseDto>(["trips", tripId]);

      // Optimistically update the spot
      if (previousTripDetail) {
        queryClient.setQueryData<TripDetailResponseDto>(["trips", tripId], {
          ...previousTripDetail,
          itineraries: previousTripDetail.itineraries.map((itinerary) => ({
            ...itinerary,
            spots: itinerary.spots.map((spot) =>
              spot.id === spotId ? { ...spot, ...input, updatedAt: new Date().toISOString() } : spot
            ),
          })),
        });
      }

      return { previousTripDetail };
    },
    onError: (_, __, context) => {
      // Rollback on error
      if (context?.previousTripDetail) {
        queryClient.setQueryData(["trips", tripId], context.previousTripDetail);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["trips", tripId] });
    },
  });
}

// Delete spot
export function useDeleteSpot(tripId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (spotId: string) => {
      const response = await apiClient.delete<null>(`/api/trips/${tripId}/spots/${spotId}`);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to delete spot");
      }
      return spotId;
    },
    onMutate: async (spotId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["trips", tripId] });

      // Snapshot previous value
      const previousTripDetail = queryClient.getQueryData<TripDetailResponseDto>(["trips", tripId]);

      // Optimistically remove the spot from the trip detail
      if (previousTripDetail) {
        queryClient.setQueryData<TripDetailResponseDto>(["trips", tripId], {
          ...previousTripDetail,
          itineraries: previousTripDetail.itineraries.map((itinerary) => ({
            ...itinerary,
            spots: itinerary.spots.filter((spot) => spot.id !== spotId),
          })),
        });
      }

      return { previousTripDetail };
    },
    onError: (_, __, context) => {
      // Rollback on error
      if (context?.previousTripDetail) {
        queryClient.setQueryData(["trips", tripId], context.previousTripDetail);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["trips", tripId] });
    },
  });
}

// Reorder spots
export function useReorderSpots(tripId: string, itineraryId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (spotIds: string[]) => {
      const response = await apiClient.patch<SpotResponseDto[]>(
        `/api/trips/${tripId}/itineraries/${itineraryId}/spots/reorder`,
        { spotIds }
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to reorder spots");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trips", tripId] });
    },
  });
}

// Move spot to different itinerary
export function useMoveSpot(tripId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      spotId,
      targetItineraryId,
      order,
    }: {
      spotId: string;
      targetItineraryId: string;
      order: number;
    }) => {
      const response = await apiClient.patch<SpotResponseDto>(
        `/api/trips/${tripId}/spots/${spotId}/move`,
        { targetItineraryId, order }
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to move spot");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trips", tripId] });
    },
  });
}

// Calculate travel times for all spots in an itinerary
export function useCalculateTravelTimes(tripId: string, itineraryId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (defaultMode: TravelMode = "driving") => {
      const response = await apiClient.post<SpotResponseDto[]>(
        `/api/trips/${tripId}/itineraries/${itineraryId}/travel-times`,
        { defaultMode }
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to calculate travel times");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trips", tripId] });
    },
  });
}

// Update travel mode for a spot
export function useUpdateTravelMode(tripId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ spotId, travelMode }: { spotId: string; travelMode: TravelMode }) => {
      const response = await apiClient.patch<SpotResponseDto>(
        `/api/trips/${tripId}/spots/${spotId}/travel-mode`,
        { travelMode }
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to update travel mode");
      }
      return response.data;
    },
    onMutate: async ({ spotId, travelMode }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["trips", tripId] });

      // Snapshot previous value
      const previousTripDetail = queryClient.getQueryData<TripDetailResponseDto>(["trips", tripId]);

      // Optimistically update the travel mode
      if (previousTripDetail) {
        queryClient.setQueryData<TripDetailResponseDto>(["trips", tripId], {
          ...previousTripDetail,
          itineraries: previousTripDetail.itineraries.map((itinerary) => ({
            ...itinerary,
            spots: itinerary.spots.map((spot) =>
              spot.id === spotId ? { ...spot, travelMode } : spot
            ),
          })),
        });
      }

      return { previousTripDetail };
    },
    onError: (_, __, context) => {
      // Rollback on error
      if (context?.previousTripDetail) {
        queryClient.setQueryData(["trips", tripId], context.previousTripDetail);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["trips", tripId] });
    },
  });
}
